import { Country } from 'country-state-city';

/**
 * Mappe les codes pays aux fuseaux horaires principaux
 */
const countryToTimezone = {
  'CA': 'America/Toronto', // Canada (par défaut Toronto, mais on peut affiner)
  'US': 'America/New_York',
  'FR': 'Europe/Paris',
  'GB': 'Europe/London',
  'DE': 'Europe/Berlin',
  'ES': 'Europe/Madrid',
  'IT': 'Europe/Rome',
  'JP': 'Asia/Tokyo',
  'CN': 'Asia/Shanghai',
  'AU': 'Australia/Sydney',
  'BR': 'America/Sao_Paulo',
  'MX': 'America/Mexico_City',
  'AR': 'America/Argentina/Buenos_Aires',
  'IN': 'Asia/Kolkata',
  'RU': 'Europe/Moscow',
  // Ajouter d'autres pays selon les besoins
};

/**
 * Mappe les villes canadiennes aux fuseaux horaires
 */
const canadianCityToTimezone = {
  'Montreal': 'America/Montreal',
  'Montréal': 'America/Montreal',
  'Quebec': 'America/Montreal',
  'Québec': 'America/Montreal',
  'Toronto': 'America/Toronto',
  'Vancouver': 'America/Vancouver',
  'Calgary': 'America/Edmonton',
  'Edmonton': 'America/Edmonton',
  'Winnipeg': 'America/Winnipeg',
  'Halifax': 'America/Halifax',
  'St. John\'s': 'America/St_Johns',
  // Ajouter d'autres villes selon les besoins
};

/**
 * Détermine le fuseau horaire basé sur le pays et la ville de l'utilisateur
 */
export const getUserTimezone = (userData) => {
  // Si pas de données utilisateur, utiliser le fuseau par défaut
  if (!userData) {
    return 'America/Montreal'; // Québec par défaut
  }

  // Si c'est le Canada, essayer de trouver le fuseau spécifique à la ville
  if (userData.country === 'CA' && userData.city) {
    const cityTimezone = canadianCityToTimezone[userData.city];
    if (cityTimezone) {
      return cityTimezone;
    }
  }

  // Sinon, utiliser le fuseau du pays
  if (userData.country && countryToTimezone[userData.country]) {
    return countryToTimezone[userData.country];
  }

  // Par défaut: Québec
  return 'America/Montreal';
};

/**
 * Formate un timestamp Unix en date lisible avec fuseau horaire
 */
export const formatTimestamp = (timestamp, userData = null) => {
  if (!timestamp) return 'Date inconnue';

  // Convertir en millisecondes si nécessaire
  const timestampMs = timestamp.toString().length === 10 ? timestamp * 1000 : timestamp;
  const date = new Date(timestampMs);

  // Obtenir le fuseau horaire
  const timezone = getUserTimezone(userData);

  try {
    // Options de formatage
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: timezone,
      timeZoneName: 'short'
    };

    // Formater selon la locale française
    return date.toLocaleString('fr-CA', options);
  } catch (error) {
    console.error('Erreur de formatage de date:', error);
    // Fallback au format ISO si erreur
    return date.toISOString();
  }
};

/**
 * Formate un timestamp pour affichage court (sans secondes)
 */
export const formatTimestampShort = (timestamp, userData = null) => {
  if (!timestamp) return 'Date inconnue';

  const timestampMs = timestamp.toString().length === 10 ? timestamp * 1000 : timestamp;
  const date = new Date(timestampMs);
  const timezone = getUserTimezone(userData);

  try {
    const options = {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timezone
    };

    return date.toLocaleString('fr-CA', options);
  } catch (error) {
    console.error('Erreur de formatage de date courte:', error);
    return date.toISOString().slice(0, 16);
  }
};

/**
 * Calcule la durée entre deux timestamps
 */
export const calculateDuration = (startTimestamp, endTimestamp) => {
  if (!startTimestamp || !endTimestamp) return '00:00';

  const start = startTimestamp.toString().length === 10 ? startTimestamp * 1000 : startTimestamp;
  const end = endTimestamp.toString().length === 10 ? endTimestamp * 1000 : endTimestamp;

  const durationMs = end - start;
  const seconds = Math.floor(durationMs / 1000) % 60;
  const minutes = Math.floor(durationMs / 60000) % 60;
  const hours = Math.floor(durationMs / 3600000);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Obtient le code du drapeau pour un pays
 */
export const getCountryFlag = (countryCode) => {
  if (!countryCode) return null;
  return countryCode.toUpperCase();
}; 